import { type FC } from 'react';

import { ChevronDown } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import { CUSTOMER_LOGOS } from '@entities/delivery';
import { CATALOG_MENU_ID, catalogMenuStore } from '@features/catalog-menu';
import { COMPANY } from '@shared/config/company';
import { pluralizeRu } from '@shared/lib';
import { tokens } from '@shared/styles/tokens';
import { Picture } from '@shared/ui/Picture';
import { CustomerLogos } from '@widgets/customer-logos';
import { DeliveryMapSection } from '@widgets/delivery-map';

import styles from './HomePage.module.css';

// Только проверяемые факты (PRODUCT.md): реестр Минпромторга, госконтракты ЕИС, ЕГРИП, каталог.
// Без цифр и рекламных оценок — цифры поставок стоят на карте
const ABOUT_POINTS = [
  {
    title: 'Собственное производство',
    text: 'Изделия внесены в реестр российской промышленной продукции Минпромторга.',
  },
  {
    title: 'Гарантия качества',
    text: 'Используем качественную сталь и порошковое покрытие.',
  },
  {
    title: 'Большой опыт',
    text: 'Поставляем оборудование школам, спортивным учреждениям и муниципальным заказчикам.',
  },
  {
    title: 'Поставка с монтажом',
    text: 'Привозим оборудование и устанавливаем его на площадке.',
  },
];

const HomePage: FC = observer(() => (
  <div className={styles.root}>
    <section className={styles.heroSection}>
      <Picture
        className={styles.heroImage}
        sizes={`(max-width: ${tokens.breakpoints.mobile}) calc(100vw - 2 * ${tokens.space.fluid(20, 5, 32)}), (max-width: ${tokens.breakpoints.desktop}) min(520px, calc(100vw - 2 * ${tokens.space.pageBase})), min(${tokens.layout.heroImageSize}, calc(min(100vw, ${tokens.layout.contentMax}) - ${tokens.layout.heroContentSize} - 2 * ${tokens.space.pageBase} - ${tokens.space[24]}))`}
        src="/hero-workout-blue-v2.webp"
        width={1254}
        height={1254}
        priority
        alt="Воркаут-комплекс РПСО: турники, шведская стенка, кольца и скамьи"
      />
      <div className={styles.heroContent}>
        <h1 className={styles.heroTitle}>
          <em>Российский производитель</em>
          <br />
          спортивного оборудования
        </h1>
        <p className={styles.heroDesc}>
          Профессиональные решения для воркаут-площадок и спортивных объектов. Высококачественная
          сталь, порошковое окрашивание.
        </p>
        <div className={styles.heroActions}>
          <button
            className={styles.ctaButton}
            onClick={() => catalogMenuStore.toggle()}
            aria-expanded={catalogMenuStore.isOpen}
            aria-controls={CATALOG_MENU_ID}
            data-catalog-trigger="true"
          >
            Смотреть каталог <ChevronDown aria-hidden />
          </button>
          <Link className={styles.ctaLink} to="/contacts">
            Запросить КП
          </Link>
        </div>
      </div>
    </section>

    <section className={styles.aboutSection} aria-labelledby="about-title">
      <h2 className={styles.sectionTitle} id="about-title">
        Производим уличное спортивное оборудование
      </h2>
      <div className={styles.aboutBody}>
        <div className={styles.aboutText}>
          <p>
            Больше десяти лет делаем уличное спортивное оборудование на своём производстве в{' '}
            {COMPANY.cityPrepositional}: тренажёры, воркаут-комплексы, спортивные и умные площадки,
            площадки для сдачи нормативов, зрительские трибуны, металлоконструкции.
          </p>
          <p>
            Работаем по госзакупкам с министерствами спорта, администрациями и школами. Поставляем
            по всей России — от Мурманска до Кубани и Сибири.
          </p>
        </div>
        <ul className={styles.aboutPoints}>
          {ABOUT_POINTS.map((point) => (
            <li key={point.title}>
              <p className={styles.aboutPointTitle}>{point.title}</p>
              <p className={styles.aboutPointText}>{point.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <DeliveryMapSection />

    {CUSTOMER_LOGOS.length > 0 && (
      <section className={styles.customersSection} aria-labelledby="customers-title">
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle} id="customers-title">
            Нам доверяют
          </h2>
          <p className={styles.sectionMeta}>
            {CUSTOMER_LOGOS.length}{' '}
            {pluralizeRu(CUSTOMER_LOGOS.length, ['заказчик', 'заказчика', 'заказчиков'])}:
            министерства спорта, администрации, спортивные школы
          </p>
        </div>
        <CustomerLogos customers={CUSTOMER_LOGOS} />
      </section>
    )}
  </div>
));

export default HomePage;
